<script setup lang="ts">
import { Roles } from '@/constants'
import consultationsHttpService from '@/services/http/consultationsHttpService'
import type { ConsultationModel, TestsAdminShortModel } from '@/services/http/httpResponses'
import usersHttpService from '@/services/http/usersHttpService'
import { isNullOrEmpty, type Id } from '@raildev/shared-utils'
import type { ButtonProps } from 'primevue/button'
import type { DataTableRowExpandEvent } from 'primevue/datatable'
import { computed, reactive, ref } from 'vue'
import { getPaidStatus, getTestStatusText } from '@/helpers/statusHelpers'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.CareerGuidanceSpecialist],
  },
})

const consultationsResponse = await consultationsHttpService.getRequests()

const consultationRequests = ref<ConsultationModel[]>(consultationsResponse.consultationRequests)

const formattedRequests = computed(() => {
  return consultationRequests.value.map((request, index) => ({
    ...request,
    order: index + 1,
    comment: isNullOrEmpty(request.comment) ? '-' : request.comment,
    createdAt: request.createdAt.toLocaleDateString('ru-RU'),
  }))
})

const expandedRows = ref({})

const testsByUserId = reactive<{
  [key: Id]: TestsAdminShortModel[]
}>({})

const loadUserTestsById = async (userId: Id) => {
  if (testsByUserId[userId]) {
    return
  }

  const response = await usersHttpService.getTestsByUserId(userId)
  testsByUserId[userId] = response.tests
}

const rowExpanded = async (e: DataTableRowExpandEvent<ConsultationModel>) => {
  const { userId } = e.data
  await loadUserTestsById(userId)
}
</script>
<template>
  <section class="flex flex-col w-full">
    <DataTable
      v-model:expandedRows="expandedRows"
      :value="formattedRequests"
      scrollable
      scrollHeight="80vh"
      @row-expand="rowExpanded"
      dataKey="id"
      :pt="{ emptyMessageCell: formattedRequests.length < 1 ? 'border-none py-12' : '' }"
    >
      <Column expander class="w-15" />
      <Column field="order" header="№" />
      <Column field="name" header="Имя пользователя" />
      <Column field="contactMethod" header="Способ связи" />
      <Column
        field="createdAt"
        header="Дата создания"
        sortable
        :pt="{
          headerCell: ({ context }) => (context.sorted ? 'bg-blue-50 text-blue-700' : ''),
          sortIcon: ({ context }) => (context.sorted ? 'text-blue-700' : ''),
        }"
      />
      <Column field="comment" header="Сообщение" />

      <template #expansion="{ data: { userId } }">
        <div v-if="testsByUserId[userId]" class="flex gap-4 flex-wrap">
          <Panel
            v-for="(test, index) in testsByUserId[userId]"
            :key="test.id"
            :toggleable="false"
            :pt="{
              header: 'border-b-1 border-[#e2e8f0] justify-center px-8 py-4',
              content: 'flex flex-col p-8',
            }"
          >
            <template #header>
              <p class="text-center font-medium">Тест № {{ index + 1 }}</p>
            </template>

            <p
              :class="
                test.billStatus === 5
                  ? 'border-green-600 text-green-600'
                  : 'border-red-600 text-red-600'
              "
              class="inline-flex p-2 border-2 rounded-lg font-medium uppercase self-center mb-6"
            >
              {{ getPaidStatus(test.billStatus) }}
            </p>
            <p class="mb-3">
              Дата старта:
              <span class="font-medium text-right">{{
                test.startDateTime.toLocaleDateString('ru-RU')
              }}</span>
            </p>
            <p>
              Статус теста:
              <span class="font-medium text-right">
                {{ getTestStatusText(test.status) }}
              </span>
            </p>
            <Button label="Открыть тест" asChild v-slot="slotProps" :disabled="!test.billStatus">
              <RouterLink
                :to="`/test-analytics/${test.id}`"
                class="mt-9 self-center font-medium"
                :class="`${(slotProps as ButtonProps).class} ${test.status != 5 ? 'opacity-60 pointer-events-none' : ''}`"
              >
                Открыть результаты
              </RouterLink>
            </Button>
          </Panel>
        </div>
      </template>
      <template #empty>
        <div class="flex flex-col gap-4 items-center">
          <span class="pi pi-search text-6xl" />
          <h3 class="font-medium">Данные о заявках не найдены</h3>
        </div>
      </template>
    </DataTable>
  </section>
</template>
<style scoped></style>
